import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getParts, createParts } from '../../api/partsAPI';
import { createStockItems } from '../../api/stock-itemsAPI';
import { createStockItemsQuantities } from '../../api/stock-items-quantitiesAPI';
import { useSetting } from '../../utils/AppSettingsContext';
import { loadSupplierOptions } from '../../utils/supplierOptionUtils';
import { useFormBuilder } from '../../utils/useFormBuilder';
import { useValidator } from '../../utils/useValidator';

const DEFAULT_CATEGORY_ID = 25;

const AddPartModal = ({
  show,
  onClose,
  onPartCreated,
  initialValues = {},
  selectedExpenseCodeId = null,
  expenseOptions = [],
}) => {
  const weightUom = useSetting('weight_uom');
  const [newPart, setNewPart] = useState({ categoryId: DEFAULT_CATEGORY_ID, lastPrice: '' });
  const [addStockItem, setAddStockItem] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  useEffect(() => {
    if (!show) return;

    setNewPart((prev) => ({
      categoryId: prev.categoryId ?? DEFAULT_CATEGORY_ID,
      lastPrice: '',
      ...initialValues,
    }));
    setAddStockItem(false);
    setSelectedSupplier(null);
  }, [initialValues, show]);

  const expenseCodePayload = useMemo(() => {
    if (!selectedExpenseCodeId || !Array.isArray(expenseOptions)) return null;
    const match = expenseOptions.find((ec) => Number(ec.id) === Number(selectedExpenseCodeId));
    return match ? { ...match } : null;
  }, [expenseOptions, selectedExpenseCodeId]);

  const resetState = () => {
    setNewPart({ categoryId: DEFAULT_CATEGORY_ID, lastPrice: '' });
    setAddStockItem(false);
    setSelectedSupplier(null);
    setNewPartErrors({});
  };

  const handleAddPartCancel = () => {
    resetState();
    onClose?.();
  };

  const handleSupplierSearch = useCallback(loadSupplierOptions, []);

  const partFields = useMemo(() => {
    const baseFields = [

      { field: 'partNumber', label: 'Part Number', type: 'text', size: 6, required: true },
      { field: 'oemNumber', label: 'OEM Number', type: 'text', size: 6 },
      { field: 'description', label: 'Description', type: 'text', size: 12, required: true },
      {
        field: 'categoryId',
        label: 'Category',
        type: 'select',
        size: 12,
        api: 'parts-category',
        displayField: 'name',
        required: true,
      },
      {
        field: 'supplierId',
        label: 'Preferred Supplier',
        type: 'paginated-select',
        size: 12,
        loadOptions: handleSupplierSearch,
        selectedOption: selectedSupplier,
        setSelectedOption: setSelectedSupplier,
        required: true,
        isClearable: true,
        minSearchLength: 2,
      },
      {
        field: 'sourceOfSupplyId',
        label: 'Part Manufacturer',
        type: 'select',
        size: 12,
        api: 'manufacturers',
        displayField: 'manufacturer',
      },
      { field: 'lastPrice', label: 'Parts Cost', type: 'number', size: 6 },
      {
        field: 'weight',
        label: `Part Weight (${weightUom})`,
        type: 'number',
        size: 6,
      },
    ];

    /*if (addStockItem) {
      baseFields.push(
        {
          field: 'weight',
          label: `Part Weight (${weightUom})`,
          type: 'number',
          size: 12,
        },
      );
    }*/

    return baseFields;
  }, [addStockItem, handleSupplierSearch, selectedSupplier, weightUom]);

  const validationFields = useMemo(
    () => [
      { field: 'partNumber', label: 'Part Number', required: true },
      { field: 'description', label: 'Description', required: true },
      { field: 'categoryId', label: 'Category', required: true },
      { field: 'lastPrice', label: 'Parts Cost' },
      ...(addStockItem ? [{ field: 'supplierId', label: 'Preferred Supplier', required: true }] : []),
    ],
    [addStockItem],
  );

  const { errors: newPartErrors, validate, setErrors: setNewPartErrors } = useValidator(validationFields, newPart);
  const { renderField } = useFormBuilder(partFields, newPart, setNewPart, newPartErrors);

  const handleAddPartSave = async () => {
    const isValid = validate();
    if (!isValid) return;

    try {
      const normalizedPartNumber = newPart.partNumber?.toLowerCase().trim();
      const searchPN = await getParts({ search: newPart.partNumber, pageSize: 10 });
      const listPN = searchPN.data || searchPN;
      let existing = listPN.find((p) => p.partNumber?.toLowerCase() === normalizedPartNumber);

      if (!existing && newPart.oemNumber) {
        const normalizedOem = newPart.oemNumber.toLowerCase().trim();
        const searchOEM = await getParts({ search: newPart.oemNumber, pageSize: 10 });
        const listOEM = searchOEM.data || searchOEM;
        existing = listOEM.find((p) => p.oemNumber?.toLowerCase() === normalizedOem);
      }

      const costValue = parseFloat(newPart.lastPrice || 0) || 0;

      if (existing) {
        onPartCreated?.({
          part: existing,
          partsCost: costValue,
          source: existing.isStocked === false || undefined ? 'PURCHASE ORDER' : 'WAREHOUSE',
          supplierId: existing.supplierId || null,
          supplier: existing.supplier || null,
          expenseCodeId: selectedExpenseCodeId ?? null,
          expenseCode: expenseCodePayload,
        });
        resetState();
        onClose?.();
        return;
      }

      const created = await createParts(newPart);

      if (addStockItem) {
        const createdStockItems = await createStockItems({
          id: null,
          partId: created.id,
          serisalised: false,
          stockMin: 0,
          stockMax: 0,
          stockDescription: created.description,
          locationId: 1,
          unitCost: costValue,
          measurementUnitId: 1,
          currency: 1,
        });
        await createStockItemsQuantities({
          id: null,
          stockItemId: createdStockItems.id,
          type: 'In Stock',
          workOrderPartsId: null,
          quantity: 0,
        });
      }

      onPartCreated?.({
        part: created,
        partsCost: costValue,
        source: addStockItem === false || undefined ? 'PURCHASE ORDER' : 'WAREHOUSE',
        supplierId: newPart.supplierId || null,
        supplier: newPart.supplier || selectedSupplier?.raw || null,
        expenseCodeId: selectedExpenseCodeId ?? null,
        expenseCode: expenseCodePayload,
      });
      resetState();
      onClose?.();
    } catch (err) {
      console.error('Error saving part:', err);
    }
  };

  return (
    show && (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog margin-top-override-48">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Part to Catalogue</h5>
              <button type="button" className="btn-close" onClick={handleAddPartCancel} />
            </div>
            <div className="modal-body" style={{ minHeight: '50vh', overflowY: 'auto' }}>
              <div className="row g-3">
                {partFields.map(renderField)}
              </div>
              <div className="form-switch switch-primary mt-12 mb-8">
                <label className="form-label">Add New Part to Stocked Items</label>
                <div className="d-flex align-items-center gap-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={addStockItem}
                    onChange={() => setAddStockItem((prev) => !prev)}
                  />
                  <label className="form-check-label line-height-1 fw-medium text-secondary-light">
                    {addStockItem ? 'Yes' : 'No'}
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleAddPartCancel}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddPartSave}>Save</button>
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default AddPartModal;
